import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DataComponentBodyFinanceBlock from './DataComponent__BodyFinanceBlock';
import DataComponentFilterFinanceBlock from './DataComponent__FilterFinanceBlock';
import DataComponentDisplayControl from './DataComponent__DisplayControl';
import DateComponentSettings from './DateComponent_Settings';
import DateComponentCurrencySettings from './DateComponent_CurrencySettings';
import Pagination from '../Pagination.js';
import query from '../../helpers/query';

const DataComponentFinanceBlock = ({
    onAction,
    totalPages,
    headerNames,
    data,
    loading,
    isQuery,
    settings,
    settingKey,
    tableBodyTitle,
    className,
    gray,
    fields,
    page,
    alignPagination,
    hideResetButton,
    currencySettings,
    currencySettingKey,
    currencySettingData,
    displayControl,
    multiLevelHeaderNames,
    columnData
}) => {
    const [filters, setFilters] = useState({});
    const [typeDisplayControl, setTypeDisplayControl] = useState('table');

    useEffect(() => {
        if (page) {
            setFilters(prev => ({ ...prev, page }));
        }
    }, [page]);

    useEffect(() => {
        if (!fields.length && onAction) {
            handleAction('firstRender');
        }
    }, [fields.length, onAction]);

    const handleAction = useCallback((actionType) => {
        if (onAction) {
            onAction(filters, actionType);
        }

        if (isQuery) {
            query.replace(filters);
        }
    }, [onAction, filters, isQuery]);

    const onPageChange = useCallback((newPage) => {
        const newFilters = { ...filters, page: newPage };
        setFilters(newFilters);
        
        if (onAction) {
            onAction(newFilters, 'pageChange');
        }
    }, [filters, onAction]);

    const onFiltersApply = useCallback((state, type = 'filtersChange') => {
        setFilters(state);
        
        if (onAction) {
            onAction(state, type);
        }
    }, [onAction]);

    const onDisplayControlChange = useCallback((type) => {
        setTypeDisplayControl(type);
    }, []);

    const getClassFooter = useCallback(() => {
        return classNames({
            'ibox-footer': true,
            'text-right': alignPagination
        });
    }, [alignPagination]);

    return (
        <div className="ibox">
            {fields.length
                ? (
                    <DataComponentFilterFinanceBlock
                        fields={fields}
                        loading={loading}
                        onAction={onFiltersApply}
                        hideResetButton={hideResetButton}
                    />
                )
                : null
            }
            <DateComponentSettings 
                settings={settings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={settingKey}
                loading={loading}
                data={data}
                filters={filters}
            />
            <DateComponentCurrencySettings
                settings={currencySettings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={currencySettingKey}
                loading={loading}
                data={currencySettingData}
                filters={filters}
            />
            <div className="ibox-content">
                {displayControl
                    ? <DataComponentDisplayControl
                        data={displayControl.data}
                        type={typeDisplayControl}
                        name={displayControl.name}
                        onChange={onDisplayControlChange}
                      />
                    : null
                }
                <DataComponentBodyFinanceBlock
                    data={data}
                    headerNames={headerNames}
                    multiLevelHeaderNames={multiLevelHeaderNames}
                    columnData={columnData}
                    loading={loading}
                    settings={settings}
                    settingKey={settingKey}
                    displayControl={{
                        ...displayControl,
                        type: typeDisplayControl
                    }}
                    filters={filters}
                    className={className}
                />
            </div>
            {totalPages > 1
                ? <div className={getClassFooter()}>
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={filters.page}
                        totalPages={totalPages}
                        loading={loading}
                        align={alignPagination ? 'right' : 'left'}
                    />
                  </div>
                : null
            }
        </div>
    );
};

DataComponentFinanceBlock.propTypes = {
    onAction: PropTypes.func,
    totalPages: PropTypes.number,
    headerNames: PropTypes.array,
    data: PropTypes.array,
    loading: PropTypes.bool,
    isQuery: PropTypes.bool,
    settings: PropTypes.bool,
    settingKey: PropTypes.string,
    tableBodyTitle: PropTypes.object,
    className: PropTypes.string,
    gray: PropTypes.bool,
    fields: PropTypes.array,
    page: PropTypes.number,
    alignPagination: PropTypes.bool,
    hideResetButton: PropTypes.bool,
    currencySettings: PropTypes.bool,
    currencySettingKey: PropTypes.string,
    currencySettingData: PropTypes.array,
    displayControl: PropTypes.shape({
        type: PropTypes.string,
        name: PropTypes.string,
        data: PropTypes.array
    }),
    multiLevelHeaderNames: PropTypes.array,
    columnData: PropTypes.shape({
        header: PropTypes.object,
        body: PropTypes.object
    })
};

DataComponentFinanceBlock.defaultProps = {
    onAction: () => {},
    totalPages: 0,
    headerNames: [],
    data: [],
    loading: false,
    isQuery: false,
    settings: false,
    settingKey: '',
    tableBodyTitle: {},
    className: '',
    gray: false,
    fields: [],
    page: 1,
    alignPagination: false,
    hideResetButton: false,
    currencySettings: false,
    currencySettingKey: '',
    currencySettingData: [],
    displayControl: null,
    multiLevelHeaderNames: [],
    columnData: {
        header: {},
        body: {}
    }
};

export default DataComponentFinanceBlock;
